/**
 * EditorToolbar
 *
 * Top toolbar for the V4 CMS editor.
 * Contains: back link, page selector, viewport toggle, zoom controls, publish button.
 */

import { useState, useCallback } from 'react';
import type { EditorViewport, EditorPageInfo, EditorMode } from '../types';

// =============================================================================
// TYPES
// =============================================================================

interface EditorToolbarProps {
  /** Business name for display */
  businessName: string;
  /** Current active page slug */
  activePage: string;
  /** List of available pages */
  pages: EditorPageInfo[];
  /** Current viewport mode */
  viewport: EditorViewport;
  /** Current zoom level (percentage) */
  zoomLevel: number;
  /** Number of unsaved changes */
  dirtyCount: number;
  /** Whether a deploy is in progress */
  deploying: boolean;
  /** Editor mode: admin shows Publish, customer shows Approve/Request Changes */
  mode?: EditorMode;
  /** Website ID for AI command bar */
  websiteId?: string;
  /** Number of available undo steps */
  undoCount?: number;
  /** Callbacks */
  onPageChange: (slug: string) => void;
  onViewportChange: (viewport: EditorViewport) => void;
  onZoomChange: (zoom: number) => void;
  onBack: () => void;
  onDeploy: () => void;
  /** Called to undo the last edit step */
  onUndo?: () => Promise<void>;
  /** Called to revert all changes to published version */
  onRevert?: () => Promise<void>;
  /** Called after structural changes from AI command bar */
  onContentChange?: () => void;
  /** Customer mode callbacks */
  onApprove?: () => void;
  onRequestChanges?: () => void;
}

// =============================================================================
// VIEWPORT ICONS (inline SVGs for clean rendering)
// =============================================================================

function DesktopIcon({ active }: { active: boolean }) {
  return (
    <svg
      className={`w-5 h-5 ${active ? 'text-blue-600' : 'text-gray-400'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function TabletIcon({ active }: { active: boolean }) {
  return (
    <svg
      className={`w-5 h-5 ${active ? 'text-blue-600' : 'text-gray-400'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}

function MobileIcon({ active }: { active: boolean }) {
  return (
    <svg
      className={`w-5 h-5 ${active ? 'text-blue-600' : 'text-gray-400'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

export function EditorToolbar({
  businessName,
  activePage,
  pages,
  viewport,
  zoomLevel,
  dirtyCount,
  deploying,
  mode = 'admin',
  websiteId: _websiteId,
  undoCount = 0,
  onPageChange,
  onViewportChange,
  onZoomChange,
  onBack,
  onDeploy,
  onUndo,
  onRevert,
  onContentChange: _onContentChange,
  onApprove,
  onRequestChanges,
}: EditorToolbarProps) {
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [undoing, setUndoing] = useState(false);

  const handleUndo = useCallback(async () => {
    if (!onUndo || undoing) return;
    setUndoing(true);
    try {
      await onUndo();
    } finally {
      setUndoing(false);
    }
  }, [onUndo, undoing]);

  const handleRevert = useCallback(async () => {
    if (!onRevert) return;
    setReverting(true);
    try {
      await onRevert();
    } finally {
      setReverting(false);
      setShowRevertConfirm(false);
    }
  }, [onRevert]);

  const handleZoomIn = useCallback(() => {
    onZoomChange(Math.min(200, zoomLevel + 25));
  }, [zoomLevel, onZoomChange]);

  const handleZoomOut = useCallback(() => {
    onZoomChange(Math.max(25, zoomLevel - 25));
  }, [zoomLevel, onZoomChange]);

  const handleZoomReset = useCallback(() => {
    onZoomChange(100);
  }, [onZoomChange]);

  return (
    <div className="h-14 bg-white border-b border-gray-200 flex items-center px-4 gap-4 shrink-0">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="text-sm font-medium">Back</span>
      </button>

      {/* Divider */}
      <div className="w-px h-6 bg-gray-200" />

      {/* Business name */}
      <span className="text-sm font-semibold text-gray-900 truncate max-w-[200px]">
        {businessName}
      </span>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Page selector */}
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500 font-medium">Page:</label>
        <select
          value={activePage}
          onChange={(e) => onPageChange(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2.5 py-1.5 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {pages.map((page) => (
            <option key={page.slug} value={page.slug}>
              {page.title} ({page.sectionCount} sections)
            </option>
          ))}
        </select>
      </div>

      {/* Divider */}
      <div className="w-px h-6 bg-gray-200" />

      {/* Viewport toggle */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
        <button
          onClick={() => onViewportChange('desktop')}
          className={`p-1.5 rounded-md transition-colors ${
            viewport === 'desktop' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
          }`}
          title="Desktop (1440px)"
        >
          <DesktopIcon active={viewport === 'desktop'} />
        </button>
        <button
          onClick={() => onViewportChange('tablet')}
          className={`p-1.5 rounded-md transition-colors ${
            viewport === 'tablet' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
          }`}
          title="Tablet (768px)"
        >
          <TabletIcon active={viewport === 'tablet'} />
        </button>
        <button
          onClick={() => onViewportChange('mobile')}
          className={`p-1.5 rounded-md transition-colors ${
            viewport === 'mobile' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
          }`}
          title="Mobile (375px)"
        >
          <MobileIcon active={viewport === 'mobile'} />
        </button>
      </div>

      {/* Divider */}
      <div className="w-px h-6 bg-gray-200" />

      {/* Zoom controls */}
      <div className="flex items-center gap-1">
        <button
          onClick={handleZoomOut}
          className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          title="Zoom out"
          disabled={zoomLevel <= 25}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
        <button
          onClick={handleZoomReset}
          className="text-xs font-medium text-gray-500 hover:text-gray-700 px-1.5 min-w-[40px] text-center"
          title="Reset zoom"
        >
          {zoomLevel}%
        </button>
        <button
          onClick={handleZoomIn}
          className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          title="Zoom in"
          disabled={zoomLevel >= 200}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Divider */}
      <div className="w-px h-6 bg-gray-200" />

      {/* Action buttons — mode-dependent */}
      {mode === 'admin' ? (
        <div className="flex items-center gap-2">
          {/* Undo button */}
          {onUndo && (
            <button
              onClick={handleUndo}
              disabled={undoCount === 0 || undoing}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                undoCount > 0 && !undoing
                  ? 'border-gray-300 text-gray-700 hover:text-blue-600 hover:border-blue-300'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
              title={undoCount > 0 ? `Undo last edit (${undoCount} steps available)` : 'Nothing to undo'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              {undoing ? 'Undoing...' : `Undo${undoCount > 0 ? ` (${undoCount})` : ''}`}
            </button>
          )}

          {/* Revert All button */}
          {onRevert && (
            <div className="relative">
              <button
                onClick={() => setShowRevertConfirm(true)}
                disabled={reverting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:text-red-600 hover:border-red-300 transition-colors"
                title="Revert all changes to published version"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Revert
              </button>

              {/* Confirmation dropdown */}
              {showRevertConfirm && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50">
                  <p className="text-sm font-medium text-gray-900 mb-1">Revert all changes?</p>
                  <p className="text-xs text-gray-500 mb-3">
                    This will discard all unpublished edits (text, images, and structural changes) and reset to the last published version.
                  </p>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setShowRevertConfirm(false)}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 rounded-md border border-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRevert}
                      disabled={reverting}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                    >
                      {reverting ? 'Reverting...' : 'Yes, Revert All'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Publish button */}
          <button
            onClick={onDeploy}
            disabled={dirtyCount === 0 || deploying}
            className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              dirtyCount > 0 && !deploying
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {deploying ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Publishing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Publish
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {onRequestChanges && (
            <button
              onClick={onRequestChanges}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Request Changes
            </button>
          )}
          {onApprove && (
            <button
              onClick={onApprove}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Approve &amp; Go Live
            </button>
          )}
        </div>
      )}
    </div>
  );
}
